import React, { useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

export type ConfirmEventDetail = {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  tone?: 'destructive' | 'warning' | 'default';
  resolve: (accepted: boolean) => void;
};

const ConfirmDialog: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('Are you sure?');
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [confirmText, setConfirmText] = useState('Confirm');
  const [cancelText, setCancelText] = useState('Cancel');
  const [tone, setTone] = useState<ConfirmEventDetail['tone']>('default');
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<ConfirmEventDetail>;
      const d = ce.detail;
      if (!d) return;
      resolverRef.current = d.resolve;
      setTitle(d.title || 'Are you sure?');
      setDescription(d.description);
      setConfirmText(d.confirmText || 'Confirm');
      setCancelText(d.cancelText || 'Cancel');
      setTone(d.tone || 'default');
      setOpen(true);
    };
    window.addEventListener('arterra:confirm', handler as EventListener);
    return () => window.removeEventListener('arterra:confirm', handler as EventListener);
  }, []);

  const resolveAndClose = (ok: boolean) => {
    const r = resolverRef.current;
    resolverRef.current = null;
    setOpen(false);
    if (r) {
      try {
        r(ok);
      } catch {
        // Intentionally ignored - resolver errors are non-critical
      }
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => {
      if (!v && open && resolverRef.current) {
        resolveAndClose(false);
      } else {
        setOpen(v);
      }
    }}>
      <AlertDialogContent className="bg-white text-neutral-900">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {(tone === 'destructive' || tone === 'warning') && (
              <AlertTriangle className={tone === 'destructive' ? 'h-5 w-5 text-red-600' : 'h-5 w-5 text-amber-500'} />
            )}
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className={description ? "text-neutral-600" : "sr-only"}>
            {description || 'Please confirm your action.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelText}</AlertDialogCancel>
          <AlertDialogAction
            className={tone === 'destructive' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
            onClick={() => resolveAndClose(true)}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDialog;
